import React, { useCallback, useContext, useMemo } from 'react';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import BokforderContext from '../BokforderContext/BokforderContext';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All } from '../../Queries/_QueryTypes/BokforderQuery';
import {
  mergeBusinessDataFormValues,
  parseText,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  useIsMediumBreakpoint,
  useIsSmallBreakpoint,
} from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import { generateMessageID } from '../../../../../Leverantor/ObjektVyer/LeverantorFaktura/Utils/AttestMeddelandeUtils';
import { TEDEKBokforderSTATUSEnum } from '../../../../../../../globalQueryTypes';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

interface IProps {}

export const AddBokforderMessageButton: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const formContext = useContext(BusinessDataFormContext);
  const bokforderContext = useContext(BokforderContext);
  const { userID } = useContext(ActiveDatabaseContext);
  const { setEditMessageID, addOrUpdateMessage } = bokforderContext;
  const isSmall = useIsSmallBreakpoint();
  const isMedium = useIsMediumBreakpoint();

  const dataMergedWithChanges = useMemo(
    () =>
      mergeBusinessDataFormValues(
        parseText(formContext?.data),
        parseText(formContext?.formState?.values ?? {})
      ),
    [formContext?.data, formContext?.formState.values]
  );

  const onClick = useCallback(() => {
    const generatedID = generateMessageID();
    setEditMessageID(generatedID);
    addOrUpdateMessage('', generatedID);
  }, [setEditMessageID, addOrUpdateMessage]);

  const messages: BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All[] =
    useMemo(
      () => (dataMergedWithChanges?._Associations?.Meddelande ?? []).reverse(),
      [dataMergedWithChanges]
    );

  const currentMessage = messages.find(
    (
      message: BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All
    ) => message.id === bokforderContext.editMessageID
  );

  const lastMessage = messages?.[0];

  const isBestallare =
    formContext?.data?._Associations?.Bestallare?.id === userID ||
    formContext?.isNew;

  const isKontrollattestant =
    formContext?.data?._Associations?.Kontrollattestant?.id === userID;

  const isInvolved =
    bokforderContext.isAttestant ||
    isBestallare ||
    isKontrollattestant ||
    bokforderContext.isReservForAttestant ||
    bokforderContext.isReservForKontrollattestant;

  const allowedStatuses = [
    TEDEKBokforderSTATUSEnum.UTKAST,
    TEDEKBokforderSTATUSEnum.EJ_GODKAND,
    TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
  ];

  const isDisabled =
    (currentMessage !== undefined && currentMessage.MEDDELANDE === '') ||
    (lastMessage !== undefined && lastMessage.MEDDELANDE === '') ||
    !isInvolved ||
    (!allowedStatuses.includes(formContext?.formState.currentValues?.STATUS) &&
      !formContext?.isNew);

  return isSmall ? (
    <EDIconButton
      color="primary"
      disabled={isDisabled}
      icon="add"
      margin="none"
      onClick={onClick}
      padding="dense"
      tooltip="Lägg till meddelande"
      testId="add-message-button"
    />
  ) : isMedium ? (
    <EDButton
      tooltip="Lägg till meddelande"
      testId="add-message-button"
      color="primary"
      disabled={isDisabled}
      flat
      onClick={onClick}
      size="medium"
      icon={
        <EDIcon color={isDisabled ? 'disabled' : 'primary'} iconType="add" />
      }
    >
      <span>Lägg till</span>
    </EDButton>
  ) : (
    <EDButton
      tooltip="Lägg till meddelande"
      testId="add-message-button"
      color="primary"
      disabled={isDisabled}
      flat
      onClick={onClick}
      size="medium"
      icon={
        <EDIcon color={isDisabled ? 'disabled' : 'primary'} iconType="add" />
      }
    >
      <span>Lägg till meddelande</span>
    </EDButton>
  );
};

export default AddBokforderMessageButton;
